import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { notification, Typography } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { useRecoilState } from 'recoil'
import { listPointState, selectedPointDetail } from 'store/buildingStore'
import Utils from 'utils'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import Button from 'components/atoms/Button'
import InitDirectionIcon from 'components/icons/InitDirectionIcon'

type Props = {
  currentSelectedPoint: string | undefined
  setBuildingMode: any
}

export default function DirectionSetting({
  currentSelectedPoint,
  setBuildingMode
}: Props) {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const { navigate } = useDirectory()

  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [, setPoints] = useRecoilState(listPointState)

  const { trigger } = useMutation(`v1/points/${currentSelectedPoint}`)

  const handleCloseSetting = () => {
    setBuildingMode((prev: any) => ({ ...prev, selectedMode: '' }))
  }

  const handleResetToInitial = () => {
    const krpano = new KrpanoEngine('full').instance
    krpano.call(
      `set('view.hlookat', 0); set('view.vlookat', 0); set('view.fov', 80);`
    )
  }

  const handleSetDirection = () => {
    const krpano = new KrpanoEngine('full').instance
    const previewKrpano = new KrpanoEngine('preview').instance
    const hlookat = krpano.get('view.hlookat')
    const vlookat = krpano.get('view.vlookat')
    const fov = krpano.get('view.fov')

    // 1. Optimistic update: Đóng panel và cập nhật UI ngay lập tức
    previewKrpano?.call(`set(initzoom,${fov});`)
    setBuildingMode((prev: any) => ({ ...prev, selectedMode: '' }))

    const targetH = Number(hlookat)

    setPointDetail((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        needsThumbnail: true,
        image360: prev.image360
          ? { ...prev.image360, needsThumbnail: true }
          : prev.image360,
        initView: {
          ...prev.initView,
          h: targetH,
          v: vlookat,
          z: fov
        }
      }
    })

    setPoints((prev) =>
      prev.map((item) => {
        if (item.id === currentSelectedPoint) {
          return {
            ...item,
            needsThumbnail: true,
            image360: item.image360
              ? { ...item.image360, needsThumbnail: true }
              : item.image360,
            initView: {
              ...item.initView,
              h: targetH,
              v: vlookat,
              z: fov
            }
          }
        }
        return item
      })
    )

    notification.success({
      message: t('building.finishedSetting')
    })

    // 2. Gửi PATCH chạy ngầm hoàn toàn trong background (không block UI, không reload Krpano)
    trigger(
      {
        method: 'patch',
        data: {
          floor_id: +(floorId || 0),
          building_id: +(id || 0),
          init_view: {
            h: hlookat,
            v: vlookat,
            z: fov,
            radar_h_gap: pointDetail?.initView.radarHGap
          }
        }
      },
      {
        onError: (error) => {
          Utils.handleErrorNavigate(error, navigate)
        }
      }
    )
  }

  const directionSettingLayout =
    'absolute w-full bottom-0 h-[135px] bg-[#021120] flex gap-[52px] justify-center items-center'

  useEffect(() => {
    const krpano = new KrpanoEngine('full').instance
    krpano.call('set(islocked, false)')
    return () => {
      krpano.call('set(islocked, true)')
    }
  }, [pointDetail?.initView])

  return (
    <div className={directionSettingLayout}>
      <div className="space-y-[18px]">
        <div className="flex gap-2">
          <InitDirectionIcon />
          <Typography className="text-[14px] leading-[14px] font-bold text-white">
            設定したい初期方向にして「設定する」ボタンをクリックしてください。
          </Typography>
        </div>

        <Typography className="text-[11px] leading-[11px] text-white">
          ※画像をドラッグすることで方向を変えることができます。
        </Typography>
      </div>

      <div className="flex gap-[10px]">
        <Button sizing="w-[160px] h-[40px]" onClick={handleCloseSetting}>
          キャンセル
        </Button>
        <Button sizing="w-[160px] h-[40px]" onClick={handleResetToInitial}>
          初期に戻す
        </Button>
        <Button
          sizing="w-[160px] h-[40px]"
          type="primary"
          onClick={handleSetDirection}
        >
          確定
        </Button>
      </div>
    </div>
  )
}
